import {Stack, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {
  startTableExport,
  TableExportFailedError,
  waitForTableExport,
} from 'instruments/connections/ConnectionDAO';
import {SnowflakeTable} from 'instruments/instrument-data/InstrumentDataConstants';
import notifications from 'notifications';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import DatalakeTableSelector from './DatalakeTableSelector';

interface DatalakeExportModalProps {
  connectionId: string;
  connectionName: string;
  // The data source the connection exports from, e.g. 'Snowflake' or 'Databricks'.
  sourceName: string;
  onRequestClose: () => void;
}

const GENERIC_FAILURE_MESSAGE = 'Failed to export table. Please try again.';

// Reports the outcome of an export that runs on after the modal closes. Large tables can take up to
// an hour, during which the user keeps working.
async function notifyWhenTableExportFinishes(
  dataFrameId: string,
  datasetName: string,
  tableName: string
): Promise<void> {
  try {
    await waitForTableExport(dataFrameId);
    notifications.success(`Created dataset "${datasetName}" from ${tableName}.`);
  } catch (err: unknown) {
    const message = err instanceof TableExportFailedError ? err.message : getUserMessage(err);
    notifications.error(
      `Exporting ${tableName} to dataset "${datasetName}" failed: ${message ?? GENERIC_FAILURE_MESSAGE}`,
      {duration: 0}
    );
    if (message == null) {
      // Unexpected, so it's reported too.
      throw err;
    }
  }
}

// Modal that lets the user pick a Snowflake or Databricks table on the connection and export it into
// a new Benchling Dataset (usable in analyses). Starts the export, then closes; a notification reports
// when the export finishes.
export function DatalakeExportModal(props: DatalakeExportModalProps): JSX.Element {
  const {connectionId, connectionName, sourceName, onRequestClose} = props;
  const [error, setError] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<SnowflakeTable | null>(null);
  const [datasetName, setDatasetName] = useState('');

  const effectiveDatasetName = datasetName.trim() || (selectedTable ? selectedTable.table_name : '');
  const canSubmit = Boolean(selectedTable && effectiveDatasetName);

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Export to dataset',
          disabled: !canSubmit,
          submit: true,
        },
      ]}
      title={`Export ${sourceName} table to dataset`}
      onRequestClose={onRequestClose}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error} : null}
      onRequestSubmit={async () => {
        setError(null);
        if (selectedTable == null) {
          return;
        }
        let dataFrameId: string;
        try {
          dataFrameId = await withLoadingScreen(
            startTableExport(
              connectionId,
              selectedTable.schema_name,
              selectedTable.table_name,
              effectiveDatasetName
            ),
            'Starting export...'
          );
        } catch (err: unknown) {
          const userMessage = getUserMessage(err);
          if (userMessage == null) {
            setError(GENERIC_FAILURE_MESSAGE);
            throw err;
          }
          setError(userMessage);
          return;
        }
        notifications.info(
          `Exporting ${selectedTable.table_name} to dataset "${effectiveDatasetName}". Large tables can take up to an hour; the dataset will appear in the connection's folder.`
        );
        onRequestClose();
        notifyWhenTableExportFinishes(dataFrameId, effectiveDatasetName, selectedTable.table_name);
      }}
    >
      <Stack spacing="md">
        <DatalakeTableSelector
          connectionId={connectionId}
          sourceName={sourceName}
          value={selectedTable}
          onChange={setSelectedTable}
        />
        <TextInput
          label="Dataset name"
          description="Defaults to the table name if left blank."
          placeholder={selectedTable ? selectedTable.table_name : `${connectionName} data`}
          value={datasetName}
          onChange={(e) => {
            setDatasetName(e.currentTarget.value);
          }}
        />
      </Stack>
    </Modal>
  );
}
